// router.js or routing configuration file
import { FullScreenLayout } from "@/components/app/FullScreenLayout";
import Layout from "@/components/app/Layout";
import Error from "@/components/error/Error";
import Auth from "@/components/sign-in/Auth";
import SignedOut from "@/components/sign-in/SignedOut";
import SignIn from "@/components/sign-in/SignIn";
import SignOut from "@/components/sign-in/SignOut";
import { Loading } from "@appkit4/react-components";
import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

const RouterLoader = () => (
  <div className="position-relative" style={{ height: 300 }}>
    <Loading className="container-loader" loadingType="circular" indeterminate={true} compact={false} />
  </div>
);

const GameCreator = lazy(() => import("@/components/game-creator/GameCreator"));
const GameEditor = lazy(() => import("@/components/game-editor/GameEditor"));
const GameSettings2 = lazy(() => import("@/components/game-editor/GameSettings2"));
const GameInfo = lazy(() => import("@/components/game-info/GameInfo"));
const GameRoomSelector = lazy(() => import("@/components/game-room-selector/GameRoomSelector"));
const CreateGameSessionForm = lazy(() => import("@/components/game-sessions/CreateGameSessionForm"));
const GameSession = lazy(() => import("@/components/game-sessions/GameSession"));
const GameSessions = lazy(() => import("@/components/game-sessions/GameSessions"));
const GameSettings = lazy(() => import("@/components/game-settings/GameSettings"));
const GameTemplate = lazy(() => import("@/components/game-template/GameTemplate"));
const GameTheme = lazy(() => import("@/components/game-theme/GameTheme"));
const Login = lazy(() => import("@/components/login/Login"));
const Register = lazy(() => import("@/components/register/Register"));
const RoomEditor = lazy(() => import("@/components/room-editor"));
const RoomPreview = lazy(() => import("@/components/room-preview/RoomPreview"));
const ScoreDashboard = lazy(() => import("@/components/score-dashboard/ScoreDashboard"));
const SessionManager = lazy(() => import("@/components/session-manager/SessionManager"));
const SessionSettings = lazy(() => import("@/components/session-settings/SessionSettings"));
const Users = lazy(() => import("@/components/users/Users"));

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "/",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameCreator />
          </Suspense>
        ),
      },
      {
        path: "/sessions",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <SessionManager />
          </Suspense>
        ),
      },
      {
        path: "/sessions/:sessionId",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <SessionSettings />
          </Suspense>
        ),
      },
      {
        path: "/sessions/create",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <CreateGameSessionForm />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/sessions/:sessionId",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <SessionSettings />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/sessions/create",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <CreateGameSessionForm />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameEditor />
          </Suspense>
        ),
      },
      {
        path: "games/create",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameTheme />
          </Suspense>
        ),
      },
      {
        path: "games/templatesetup",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameTemplate />
          </Suspense>
        ),
      },
      {
        path: "games/roomselection",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameRoomSelector />
          </Suspense>
        ),
      },
      {
        path: "games/basicinformation",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameInfo />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/advancedmode",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameSettings />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/edit",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameEditor />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/settings",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameSettings2 />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/sessions",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameSessions />
          </Suspense>
        ),
      },
      {
        path: "games/:gameId/rooms/:roomId",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <RoomEditor />
          </Suspense>
        ),
      },
      {
        path: "/users",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <Users />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "",
    element: <FullScreenLayout />,
    children: [
      {
        path: "games/:gameId/rooms/:roomId/preview",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <RoomPreview />
          </Suspense>
        ),
      },
      {
        path: "sessions/:sessionId/play",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <GameSession />
          </Suspense>
        ),
      },
      {
        path: "sessions/:sessionId/play/scoredashboard",
        element: (
          <Suspense fallback={<RouterLoader />}>
            <ScoreDashboard />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "login",
    element: (
      <Suspense fallback={<RouterLoader />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: "signin",
    element: <SignIn />,
  },
  {
    path: "authorize",
    element: <Auth />,
  },
  {
    path: "register",
    element: (
      <Suspense fallback={<RouterLoader />}>
        <Register />
      </Suspense>
    ),
  },

  {
    path: "signout",
    element: <SignOut />,
  },
  {
    path: "signedout",
    element: <SignedOut />,
  },
  {
    path: "error",
    element: <Error />,
  },
]);

export default router;
